import { useCallback, useEffect, useState } from 'react';
import { onValue, ref, update } from 'firebase/database';
import { db, getEnvDbPrefix } from '@/core/firebase/config';

type SeenChapters = Record<string, number>;

export default function useOnboardingStatus(userId: string | null | undefined) {
  const [seen, setSeen] = useState<SeenChapters>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const dbStringRef = getEnvDbPrefix(`user/${userId}/onboarding/seen`);

  useEffect(() => {
    if (!userId) {
      setSeen({});
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const unsubscribe = onValue(
      ref(db, dbStringRef),
      snapshot => {
        setSeen((snapshot.val() as SeenChapters | null) ?? {});
        setError(null);
        setIsLoading(false);
      },
      err => {
        console.error('Error fetching onboarding status:', err);
        setError(err);
        setIsLoading(false);
      }
    );
    return () => unsubscribe();
  }, [userId, dbStringRef]);

  const markSeen = useCallback(
    async (slideIds: string[]) => {
      if (!userId || slideIds.length === 0) return;
      const seenAt = Date.now();
      await update(ref(db, dbStringRef), Object.fromEntries(slideIds.map(id => [id, seenAt])));
    },
    [userId, dbStringRef]
  );

  return { seen, isLoading, error, markSeen };
}
